import { PulseRoutes } from "../interfaces/interfaces";
import {
  PulseOverviewBooksConnection,
  PulseOverviewCardConfig,
  PulseOverviewCategory,
  PulseCountedTabCategory,
  PulseTabConfig,
  PulseTabKey,
} from "../interfaces/pulse.interfaces";

export const PULSE_OVERVIEW_ERROR_MESSAGE = "Failed to fetch Pulse overview";
export const PULSE_ACCESS_API_ENDPOINT = "/pulse/access";
export const PULSE_OVERVIEW_API_ENDPOINT = "/pulse/overview";
export const PULSE_OVERVIEW_API_DATE_FORMAT = "DD-MM-YYYY";
export const PULSE_OVERVIEW_DISPLAY_DATE_FORMAT = "MMM D";
export const PULSE_BOOKS_API_ENDPOINT = "/pulse/books";
export const PULSE_BOOKS_ERROR_MESSAGE = "Unable to load books";
export const PULSE_BOOKS_API_DATE_FORMAT = "YYYY-MM-DD";
export const PULSE_BOOKS_DUE_DATE_FORMAT = "MMM D";
export const PULSE_BOOKS_LAST_SYNC_DATE_FORMAT = "ddd D MMM";
export const PULSE_BOOKS_ZOHO_OAUTH_SOURCE = "pulse-books";
export const PULSE_BOOKS_ZOHO_SYNC_JOB_TYPE = "zoho_push";
export const PULSE_MONITORING_BANK_ACCOUNTS_API_ENDPOINT =
  "/pulse/monitoring/bank-accounts";
export const PULSE_MONITORING_BANK_ACCOUNTS_ERROR_MESSAGE =
  "Unable to load bank statements";
export const PULSE_MONITORING_API_DATE_FORMAT = "YYYY-MM-DD";
export const PULSE_MONITORING_DISPLAY_DATE_FORMAT = "MMM D";
export const PULSE_DEADLINE_SUBMITTED_SOCKET_EVENT =
  "PULSE_DEADLINE_SUBMITTED";
export const PULSE_BANK_STATEMENT_DATA_TYPE = "BANK_STATEMENT";
export const PULSE_OVERVIEW_RED_THRESHOLD_DAYS = 3;
export const PULSE_OVERVIEW_ESTIMATED_MINUTES = 9;
export const PULSE_OVERVIEW_MAX_FIRST_NAME_LENGTH = 25;
export const PULSE_CONNECTIONS_CATEGORY = "connections";
export const PULSE_COUNTED_TAB_CATEGORIES: PulseCountedTabCategory[] = [
  "connections",
  "gst",
  "bankStatements",
  "books",
  "financials",
  "investments",
];
export const PULSE_PROGRESS_DEFAULT_COMPLETED_COUNT = 1;
export const PULSE_PROGRESS_DEFAULT_TOTAL_COUNT = 6;

export const PULSE_OVERVIEW_LABELS = {
  needsActionNow: "Needs Action Now",
  dueToday: "Due Today",
  allSet: "All Set",
  dueTodayBadge: "Due today",
  updatedBadge: "Updated",
  allConnectedBadge: "All Connected",
  overdueBadge: "Overdue",
  nextDueBadge: "Next due",
  promisedByBadge: "Promised by",
  greetingSuffix: "here's what needs reporting today.",
} as const;

export const PULSE_OVERVIEW_ALL_SET_TITLES: Partial<
  Record<PulseOverviewCategory, string>
> = {
  connections: "Sales & Ads channels",
  gst: "GST Accounts",
  financials: "Financials",
  investments: "Debt & Investments",
  bankStatements: "Bank statements",
};

export const PULSE_OVERVIEW_BOOK_TITLES: Record<
  PulseOverviewBooksConnection,
  string
> = {
  TALLY: "Tally update",
  ZOHO: "Zoho update",
  TALLY_ZOHO: "Books",
};

export const PULSE_OVERVIEW_ALL_CONNECTED_CATEGORIES: PulseOverviewCategory[] =
  ["connections", "gst"];

export const PULSE_BANK_STATEMENTS_LABELS = {
  dueToday: "Due today",
  overdue: "Overdue",
  updated: "Updated",
  nextDue: "Next due",
  requiredPeriodPrefix: "For",
} as const;

export const PULSE_BOOKS_LABELS = {
  title: "Books",
  description: "Connect your accounting software.",
  tallyDescriptionPrefix: "Update your entries in Tally, then click",
  tallyDescriptionEmphasis: "Sync with Merito",
  tallyDescriptionSuffix: "from the Tally menu.",
  tallySyncHelp: "Trouble syncing?",
  tallySetupGuide: "View setup guide →",
  tally: "Tally",
  zoho: "Zoho Books",
  lastSync: "Last sync",
  updated: "Updated",
  nextDue: "Next due",
  dueToday: "Due today",
  overdue: "Overdue",
  zohoDisconnected: "Zoho Books is disconnected.",
  connectZoho: "Connect Zoho Books",
  reconnectHelper: "You'll be redirected to Zoho to log in.",
  zohoConnectFailed: "Could not connect to Zoho Books. Please try again.",
  zohoNoOrganizations: "No Zoho organizations were found for this login.",
  zohoOrganizationsInvalid:
    "Could not read the Zoho organizations returned. Please try connecting again.",
  zohoConfirmFailed:
    "Could not finish connecting Zoho Books. Please try again.",
  zohoConnected: "Zoho Books connected",
} as const;

export const PULSE_FINANCIALS_API_ENDPOINT = "/pulse/financials";
export const PULSE_FINANCIALS_DOCUMENTS_API_ENDPOINT =
  "/pulse/financials/documents";
export const PULSE_DEADLINES_API_ENDPOINT = "/pulse/deadlines";
export const PULSE_FINANCIALS_CLASSIFIED_SOCKET_EVENT =
  "PULSE_FINANCIALS_CLASSIFIED";
export const PULSE_FINANCIALS_ERROR_MESSAGE = "Unable to load financials";
export const PULSE_FINANCIALS_FILE_INPUT_ACCEPT = ".pdf,.xls,.xlsx";
export const PULSE_FINANCIALS_EXCEL_MIME_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
export const PULSE_FINANCIALS_ALLOWED_MIME_TYPES = [
  "application/pdf",
  ...PULSE_FINANCIALS_EXCEL_MIME_TYPES,
];

export const PULSE_FINANCIALS_LABELS = {
  title: "Financials",
  description: "These can be provisional, unaudited figures.",
  cardTitles: { MIS: "MIS", BALANCE_SHEET: "Balance Sheet" },
  uploadFiles: "Upload Files",
  notYet: "Not yet",
  expectedBy: "Expected By",
  confirm: "Confirm",
  uploadNowInstead: "Upload Now Instead",
  promisedBy: "Promised by",
  wrongPeriodTitle: "This file doesn't cover the right period.",
  wrongPeriodHelpPrefix: "Please upload report for",
  invalidFileType: "Only PDF and Excel (.xls, .xlsx) files can be uploaded.",
  invalidPromiseDatePrefix: "Pick a date after today and on or before",
} as const;

export const PULSE_OVERVIEW_CARD_CONFIGS: PulseOverviewCardConfig[] = [
  {
    category: "connections",
    title: "Connections",
    targetPath: PulseRoutes.connections,
    pendingActionTitle: {
      singular: "channel",
      plural: "channels",
      suffix: "disconnected",
    },
  },
  {
    category: "gst",
    title: "GST",
    targetPath: PulseRoutes.gst,
    pendingActionTitle: {
      singular: "GST",
      plural: "GSTs",
      suffix: "disconnected",
    },
  },
  {
    category: "financials",
    title: "Financials update",
    targetPath: PulseRoutes.financials,
  },
  {
    category: "bankStatements",
    title: "Upload bank statements",
    targetPath: PulseRoutes.bankStatements,
  },
  // PULSE_BOOKS_DISABLED: re-enable when books returns to Pulse
  // {
  //   category: "books",
  //   title: "Tally update",
  //   targetPath: PulseRoutes.books,
  // },
  // PULSE_INVESTMENTS_DISABLED: re-enable when investments ships
  // {
  //   category: "investments",
  //   title: "Debt & Investments",
  //   targetPath: PulseRoutes.debtInvestments,
  // },
];

export const PULSE_TABS: PulseTabConfig[] = [
  { key: "overview", label: "Overview", to: PulseRoutes.overview },
  {
    key: "connections",
    label: "Connections",
    to: PulseRoutes.connections,
    badgeCategory: "connections",
  },
  {
    key: "gst",
    label: "GST",
    to: PulseRoutes.gst,
    badgeCategory: "gst",
  },
  {
    key: "bankStatements",
    label: "Bank Statements",
    to: PulseRoutes.bankStatements,
    badgeCategory: "bankStatements",
  },
  // PULSE_BOOKS_DISABLED: re-enable when books returns to Pulse
  // {
  //   key: "books",
  //   label: "Books",
  //   to: PulseRoutes.books,
  //   badgeCategory: "books",
  // },
  {
    key: "financials",
    label: "Financials",
    to: PulseRoutes.financials,
    badgeCategory: "financials",
  },
  // PULSE_INVESTMENTS_DISABLED: re-enable when investments ships
  // {
  //   key: "debtInvestments",
  //   label: "Debt & Investments",
  //   to: PulseRoutes.debtInvestments,
  //   badgeCategory: "investments",
  // },
];

export const PULSE_EMPTY_TAB_DATA: Record<PulseTabKey, any | null> = {
  connections: null,
  gst: null,
  bankStatements: null,
  books: null,
  financials: null,
  debtInvestments: null,
};
